import Link from "next/link";
import { useRouter } from "next/router";
import Img from "next/image";
import { documentToReactComponents } from "@/components/content/rich-text";
import { BLOCKS, MARKS } from "@/components/content/rich-text";
import Image from "@/components/image";
import { richContentOptions } from "@/components/content-options";
import { useSession } from "@/components/providers/session";
import { format } from "date-fns/format";
import { useState, useEffect, useMemo, Fragment } from "react";
import Modal from "@/components/modal";
import {
  ArrowRightCircleIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { PhoneIcon } from "@heroicons/react/24/solid";
import { currency } from "@/utils/formats";
import {
  formatOnsitePrice,
  getOnsiteDisplayPricePence,
} from "@/utils/onsite-vat-display";
import OnlineCoursesListing from "@/components/online-courses-listing";
import { resolveOnlineBasketPricePence } from "@/lib/online-basket-checkout";
import QuizPromo from "./quiz-promo";
import { TRAINING_HUB_PATH } from "@/config/training-personas";
import RegisterInterestForm from "./register-interest-form";
import { isGhostVtctCourse } from "@/utils/vtct-ghost-courses";
import {
  resolveCourseCanonicalUrl,
  resolveCourseCategoryHubPath,
} from "@/utils/course-canonical-urls";
import {
  dedupeArticlesByUrl,
  extractPlainTextFromRichText as extractPlainText,
  getArticleExcerpt,
  getArticlePublishedAt,
  getArticlePublishedLabel,
  getArticleReadTime,
  inferArticleTheme,
  slugifyTheme,
} from "@/utils/blog-themes";

function courseListingHref(url: string | null | undefined): string {
  return resolveCourseCanonicalUrl(url) || url || "";
}

const PATHWAY_PAGE_PATH = "/pathway-to-aesthetics/";
const PATHWAY_IMAGE_ALT = "Pathway to Aesthetics course";
// Old Fast Track entry, renamed in content as the hub Pathway card. Its CRM
// event_detail (aee2ae06) is still the retired Fast Track product, so Book now
// sends learners to the Pathway page until the CRM record is repointed.
const PATHWAY_HUB_CARD_CONTENTFUL_ID = "6u81cZxcK5kM0o6DdliQoN";
// Kept published so the price list and finance catalogue still list the
// package; only its hub card is dropped so hubs show one Pathway card.
const PATHWAY_PACKAGE_CONTENTFUL_ID = "5UUkUJIbOwCrD1bpdSAbmY";

function isListingHiddenCourse(course: {
  sys?: { id?: string };
  fields?: { hidden?: boolean; data?: { redirected?: boolean } };
}): boolean {
  return (
    course.fields?.hidden === true ||
    course.fields?.data?.redirected === true ||
    course.sys?.id === PATHWAY_PACKAGE_CONTENTFUL_ID
  );
}

const Bold = (text) => (
  <strong className="font-medium text-black">{text}</strong>
);

const Heading6 = (node, children) => (
  <h6 className="text-sm font-medium text-brand-600">{children}</h6>
);

const P = (node, children) => (
  <p className="first:flex-1 mt-3 first:mt-0 text-gray-500 text-sm lg:text-sm">
    {children}
  </p>
);

// Note: Google's 60-character limit is for DISPLAY, not schema.
// Use full description in schema markup for better SEO.

const formatAmount = (exVatPence: number) => {
  const displayPence = getOnsiteDisplayPricePence(exVatPence);
  if (displayPence == null) {
    return "";
  }

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(displayPence / 100);
};

const options = {
  ...richContentOptions,
  renderMark: {
    ...richContentOptions.renderMark,
    [MARKS.BOLD]: Bold,
  },
  renderNode: {
    ...richContentOptions.renderNode,
    [BLOCKS.HEADING_6]: Heading6,
    [BLOCKS.PARAGRAPH]: P,
  },
};

function I({ fields, index = 99 }) {
  if (
    !fields.listingImage ||
    !fields.listingImage.fields ||
    !fields.listingImage.fields.file
  ) {
    return null;
  }
  
  // Check if details.image exists (for CMS images) or use default aspect ratio
  const imageDetails = fields.listingImage.fields.file.details?.image;
  const aspect = imageDetails 
    ? imageDetails.height / imageDetails.width
    : 16 / 9; // Default aspect ratio for local articles
  const aspectClass =
    aspect === 1
      ? "aspect-w-16 aspect-h-9 md:aspect-1"
      : "aspect-w-16 aspect-h-9 md:aspect-1"; //"aspect-w-12 aspect-h-7";
  const imgClass =
    aspect === 1
      ? "rounded-t-lg shadow object-cover object-center"
      : "rounded-t-lg object-cover object-center shadow";
  const listingHref = courseListingHref(fields.url);

  return (
    <div className="relative text-base w-full">
      <figure>
        <Link href={listingHref} className="cursor-pointer">
          <div className={`${aspectClass}`}>
            <Image
              alt={listingHref === PATHWAY_PAGE_PATH ? PATHWAY_IMAGE_ALT : ""}
              priority={index < 3}
              id="JHVHJVG"
              fields={fields.listingImage}
              className={imgClass}
              fill
            />
          </div>
        </Link>
      </figure>
    </div>
  );
}

export const CourseItem = ({
  course,
  rest,
  session,
  router,
  cat,
  index,
  interest = false,
}: any) => {
  const cats = course.fields.categories || [];
  const isVTCT = cats.includes("vtct");
  const isArticle = course.fields.type && course.fields.type === "Article";
  const courseHref = courseListingHref(course.fields.url);
  const eventData =
    rest.events && rest.events.length > 0
      ? rest.events.find((x) => x.contentful_id === course.sys.id)
      : {
          events: [],
        };

  if (eventData && eventData.events) {
    eventData.events =
      eventData && eventData.events
        ? eventData.events.filter((x) =>
            x.countStudentRegistrations && x.countStudentRegistrations.aggregate
              ? x.seats - x.countStudentRegistrations.aggregate.count > 0
              : false
          )
        : [];
  }

  const wasPrice =
    eventData && eventData.wasPrice > 0 ? eventData.wasPrice : null;

  const standardPrice =
    eventData && eventData.standardPrice ? eventData.standardPrice : null;

  const eventPrice =
    eventData && eventData.events.length > 0 ? eventData.events[0].price : null;

  const nextPrice =
    eventPrice && standardPrice && eventPrice < standardPrice
      ? `Was: ${formatAmount(standardPrice)} - Now: ${formatAmount(eventPrice)}`
      : wasPrice && eventPrice && eventPrice < wasPrice
      ? `Was: ${formatAmount(wasPrice)} - Now: ${formatAmount(eventPrice)}`
      : eventPrice
      ? `Price: ${formatAmount(eventPrice)}`
      : wasPrice && standardPrice && standardPrice < wasPrice
      ? `Was: ${formatAmount(wasPrice)} - Now: ${formatAmount(standardPrice)}`
      : `Price: ${formatAmount(standardPrice)}`;

  const nextCourse =
    eventData &&
    eventData.events &&
    eventData.events.length > 0 &&
    eventData.events.find(
      (x) =>
        (x.countStudentRegistrations?.aggregate?.count ??
          x.registrations?.length ??
          0) < x.seats
    );

  async function getEvent() {
    try {
      const req = await fetch(`/api/course-search/?sysId=${course.sys.id}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const res = await req.json();

      const e = res.event;
      const events = res.event.events.filter(
        (x) =>
          (x.countStudentRegistrations?.aggregate?.count ??
            x.registrations?.length ??
            0) < x.seats
      );

      session.addCourse({
        ...course,
        event: e,
        package: e && e.package,
        selectedEvent: events && events[0] ? events[0] : null,
      });
      setTimeout(() => {
        window.location.href = "/checkout";
      }, 100);
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div
      className="relative flex flex-col rounded-lg shadow-md shadow-gray-300 overflow-hidden border-l-2 md:border-l-4 border-brand-600"
      key={course.sys.id}
    >
      {!isArticle && nextPrice && nextCourse && (
        <div className="flex mb-2 absolute top-2 right-2 z-10">
          <div className="bg-white relative flex items-center gap-x-2 rounded-full px-4 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-gray-300 transition-all duration-200 hover:shadow-lg hover:shadow-brand-400">
            <span className="font-semibold text-brand-600 ">
              {formatOnsitePrice(nextCourse.seat_price)}
            </span>
            <span className="h-4 w-px bg-gray-900/10" aria-hidden="true" />
            <Link
              href={courseHref}
              className="flex items-center gap-x-1 glow text-xs font-semibold"
            >
              <span className="absolute inset-0" aria-hidden="true" />
              Next course: {format(new Date(nextCourse.date), "do MMMM")}
              <ChevronRightIcon
                className="-mr-2 h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Link>
          </div>
        </div>
      )}
      {!isArticle && nextPrice && !nextCourse && (
        <div className="flex mb-2 absolute top-2 right-2 z-10">
          <div className="bg-white relative flex items-center gap-x-2 rounded-full px-4 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-gray-300 transition-all duration-200 hover:shadow-lg hover:shadow-brand-400">
            <PhoneIcon
              className="h-4 w-4 flex-shrink-0 text-brand-600"
              aria-hidden="true"
            />
            <span className="h-4 w-px bg-gray-900/10" aria-hidden="true" />
            <a
              href="tel:+443330155117"
              className="flex items-center gap-x-1 glow text-xs font-semibold"
            >
              Call now to schedule
              <ChevronRightIcon
                className="-mr-2 h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </a>
          </div>
        </div>
      )}
      {course.fields.listingImage && <I index={index} fields={course.fields} />}
      {isVTCT ? (
        <div className="w-16 h-12 absolute top-0 left-0 z-10 bg-white rounded-br p-4">
          <Img
            src="/logo/vtct.png"
            fill
            alt="VTCT Approved Training At The Cosmetic College"
          />
        </div>
      ) : null}
      <div className="flex-1 bg-white p-4 lg:p-6 flex flex-col justify-between">
        <div className="flex-1 flex flex-col">
          <Link href={courseHref}>
            <h3 className="text-base md:text-xl font-bold cursor-pointer text-gray-900">
              {course.fields.title}
            </h3>
          </Link>{" "}
          {course.fields.listingText && (
            <Link href={courseHref} className="md:sr-only">
              <div className="mt-3 text-sm cursor-pointer text-gray-500 line-clamp-3 md:sr-only">
                {documentToReactComponents(course.fields.listingText, options)}
              </div>
            </Link>
          )}
          {!isArticle ? (
            <>
              <div className="flex items-center space-x-4 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    if (course.sys.id === PATHWAY_HUB_CARD_CONTENTFUL_ID) {
                      router.push(PATHWAY_PAGE_PATH);
                      return;
                    }
                    getEvent();
                  }}
                  className="w-full md:w-max rounded-md bg-brand-600 px-1 py-2 md:p-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500 flex-1"
                >
                  <span className="relative z-[1]">Book now</span>
                </button>
                <Link
                  href={courseHref}
                  className="w-full md:w-max rounded-md bg-gray-100 px-1 py-2 md:p-3 text-sm font-semibold text-gray-600 shadow-sm hover:bg-gray-50 outline outline-2 outline-offset-2 outline-gray-300 transition-colors duration-200 hover:outline-brand-500 flex items-center justify-center flex-1 text-center"
                >
                  <span className="relative z-[1]">Course details</span>
                  <ArrowRightCircleIcon className="w-4 ml-0.5 relative z-[1]" />
                </Link>
              </div>
            </>
          ) : (
            <Link
              href={courseHref}
              className="group relative w-full inline-flex items-center justify-center px-5 py-2 border border-gray-200 rounded-md shadow text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 stretch-link mt-3"
            >
              Read more
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

const ArticleItem = ({ course, cat, index }) => {
  const isArticle = course.fields.type && course.fields.type === "Article";
  const courseHref = courseListingHref(course.fields.url);

  if (!isArticle) return null;

  return (
    <div
      className="relative flex flex-col rounded-lg shadow-md shadow-gray-300 overflow-hidden border-l-2 md:border-l-4 border-brand-600"
      key={course.sys.id}
    >
      {course.fields.listingImage && <I index={index} fields={course.fields} />}
      
      <div className="flex-1 bg-white p-4 lg:p-6 flex flex-col justify-between">
        <div className="flex-1 flex flex-col">
          <Link href={courseHref}>
            <h3 className="text-base md:text-xl font-bold cursor-pointer text-gray-900 hover:text-brand-600 transition-colors">
              {course.fields.title}
            </h3>
          </Link>
          
          {course.fields.listingText && (
            <Link href={courseHref} className="mt-3">
              <div className="text-sm cursor-pointer text-gray-500 line-clamp-3 hover:text-gray-700 transition-colors">
                {documentToReactComponents(course.fields.listingText, options)}
              </div>
            </Link>
          )}
          
          {course.fields.author && (
            <div className="mt-2 text-xs text-gray-400">
              By {course.fields.author}
            </div>
          )}
          
          {course.fields.publishedDate && (
            <div className="mt-1 text-xs text-gray-400">
              {format(new Date(course.fields.publishedDate), "do MMMM yyyy")}
            </div>
          )}
          
          <Link
            href={courseHref}
            className="group relative w-full inline-flex items-center justify-center px-5 py-2 border border-gray-200 rounded-md shadow text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 stretch-link mt-4"
          >
            Read article
            <ArrowRightCircleIcon className="w-4 ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const OnlineCourseItem = ({ course, cat, setInterested }) => {
  const { addOnlineCourses, setContext } = useSession();
  const isArticle = course.fields.type && course.fields.type === "Article";
  const courseData = course.fields?.data ?? {};
  const pricePence = resolveOnlineBasketPricePence(course);
  const courseHref = courseListingHref(course.fields.url);

  const priceLabel =
    courseData.active === false && courseData.pre
      ? `Pre Release Special Price: £${courseData.pre}`
      : pricePence
        ? `Price: ${formatAmount(pricePence)}`
        : courseData.full
          ? `Price: £${courseData.full}`
          : null;

  const handleBuy = (event) => {
    event.preventDefault();
    addOnlineCourses(course, { online: true });
    setContext((state) => ({
      ...state,
      onlineBasket: !state.onlineBasket,
    }));
  };

  const priceBlock =
    courseData.was && pricePence ? (
      <div className="absolute right-1 top-1 bg-brand-600  text-white rounded-full py-1 px-2 shadow shadow-gray-500 z-10">
        <p className="text-sm">
          Was: {formatAmount(courseData.was)} - Now {formatAmount(pricePence)}
        </p>
      </div>
    ) : priceLabel ? (
      <div className="absolute right-1 top-1 bg-brand-600  text-white rounded-full py-1 px-2 shadow shadow-gray-500 z-10">
        <p className="text-sm">{priceLabel}</p>
      </div>
    ) : null;

  return (
    <div
      className="relative flex flex-col rounded-lg shadow-lg overflow-hidden"
      key={course.sys.id}
    >
      {!isArticle && priceLabel ? <>{priceBlock}</> : null}
      {course.fields.listingImage && (
        <Link href={courseHref}>
          <div className="shrink-0 relative aspect-w-16 aspect-h-9">
            <Image
              className="w-full object-cover"
              fields={course.fields.listingImage}
              fill
              alt={course.fields.title}
            />
          </div>
        </Link>
      )}
      <div className="flex-1 bg-white p-4 lg:p-6 flex flex-col justify-between">
        <div className="flex-1 flex flex-col">
          <p className="text-sm font-medium text-brand-600">
            <Link href={courseHref}>
              <span className="hover:underline">{cat || "Course"}</span>
            </Link>
          </p>
          <h3 className="text-xl font-semibold text-gray-900 mt-2">
            {course.fields.title}
          </h3>
          {course.fields.listingText && (
            <div className="mt-3 text-base text-gray-500 flex-1 flex flex-col">
              {documentToReactComponents(course.fields.listingText, options)}
            </div>
          )}

          {courseData.release && !courseData.active && (
            <div className="mt-3 text-base text-gray-500 flex flex-col">
              <p className="text-sm font-medium text-gray-500">
                Release date: {courseData.release}
              </p>
            </div>
          )}

          {!isArticle ? (
            <>
              {courseData.partner ? (
                <Link
                  href={courseHref}
                  className="group relative w-full inline-flex items-center justify-center px-5 py-2 border border-transparent rounded-md shadow text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 stretch-link mt-3"
                >
                  Buy Course
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={handleBuy}
                  className="group relative w-full inline-flex items-center justify-center px-5 py-2 border border-transparent rounded-md shadow text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 stretch-link mt-3"
                >
                  {courseData.active === false ? "Pre Order Now" : "Buy Course"}
                </button>
              )}
              <Link
                href={courseHref}
                className="group relative w-full inline-flex items-center justify-center px-5 py-2 border border-gray-200 rounded-md shadow text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 stretch-link mt-3"
              >
                View course details
              </Link>
            </>
          ) : (
            <Link
              href={courseHref}
              className="group relative w-full inline-flex items-center justify-center px-5 py-2 border border-gray-200 rounded-md shadow text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 stretch-link mt-3"
            >
              Read more
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

function SeperatedListing({ courses, blog, localArticles = [], ...rest }) {
  const router = useRouter();
  const session = useSession();
  const [interested, setInterested] = useState<any>(false);

  const cat = rest.entry && rest.entry.fields && rest.entry.fields.title;
  
  // Filter articles when blog is true - include both CMS and local articles
  const cmsArticles = blog && courses?.items ? courses.items.filter(course => 
    course.fields?.type === "Article"
  ) : [];
  
  // Local articles first; drop CMS cards that share the same URL.
  const articles = dedupeArticlesByUrl([...localArticles, ...cmsArticles]);

  // const { pre, post } = courses.items.reduce(
  //   (c, a) => {
  //     if (a.fields.categories.includes("online")) {
  //       if (a.fields.data.active) {
  //         return {
  //           pre: c.pre,
  //           post: [...c.post, a],
  //         };
  //       } else {
  //         return {
  //           pre: [...c.pre, a],
  //           post: c.post,
  //         };
  //       }
  //     } else {
  //       return {
  //         pre: [],
  //         post: [...c.post, a],
  //       };
  //     }
  //   },
  //   { pre: [], post: [] }
  // );

  if (rest.entry.fields.url === "/courses/vtct") {
    return null;
  }

  const vtct = courses.items.filter((course) => {
    return (
      course.fields.categories.includes("vtct") && !isGhostVtctCourse(course)
    );
  });

  const nonVtct = courses.items.filter((course) => {
    return (
      !course.fields.categories.includes("vtct") &&
      course.fields.data &&
      course.fields.data.redirected !== true
    );
  });

  let navLinks = [...vtct, ...nonVtct].reduce(
    (a, b) => {
      if (isGhostVtctCourse(b)) {
        return a;
      }

      const tt = b.fields.title;
      const matches = tt.match(/(\d+)/);
      if (matches && matches.length > 0) {
        const num = matches[0];
        if (!a[num]) {
          return {
            ...a,
            [num]: [b],
          };
        } else {
          return {
            ...a,
            [num]: [...a[num], b],
          };
        }
      } else {
        return {
          ...a,
          other: [...a.other, b],
        };
      }
    },
    {
      other: [],
    }
  );

  const collection = vtct.sort((a, b) => b.fields.level - a.fields.level);

  // Add articles to collection when blog is true - local articles first
  const allItems = blog ? [...articles, ...collection] : collection;

  const interestCollection = vtct.filter((course) => {
    const e = rest.events.find((x) => x.contentful_id === course.sys.id);
    return e && e.events && e.events.length === 0;
  });
    
  // Add articles to interest collection when blog is true - local articles first
  const allInterestItems = blog && interestCollection ? [...articles, ...interestCollection] : interestCollection;

  return (
    <>
      <section className="container mx-auto relative bg-gray-50 pb-20 px-4 sm:px-6 lg:pt-0 lg:pb-28">
        <div className="absolute inset-0">
          <div className="bg-white h-1/3 sm:h-2/3"></div>
        </div>
        <div className="relative max-w-7xl mx-auto flex">
          <div className="hidden lg:block sticky lg:top-0 lg:mr-8 lg:w-1/5 lg:mt-6 lg:-ml-4">
            <h3 className="text-lg font-extrabold text-gray-900 sm:tracking-tight mb-4">
              {cat}
            </h3>
            <ul className="space-y-3">
              {Object.keys(navLinks).map((key) => {
                return (
                  <Fragment key={`nav-${key}`}>
                    {key !== "other" ? (
                      <p className="font-semibold">Level {key}</p>
                    ) : (
                      ""
                    )}
                    {navLinks[key]
                      .sort((a, b) =>
                        a.fields.title.localeCompare(b.fields.title)
                      )
                      .map((course) => {
                        return (
                          <li key={`list-${course.sys.id}`} className="text-xs">
                            <Link
                              href={courseListingHref(course.fields.url)}
                              className="hover:text-brand-500 transition-colors duration-150"
                            >
                              {course.fields.title}
                            </Link>
                          </li>
                        );
                      })}
                  </Fragment>
                );
              })}
            </ul>
          </div>
          <div className="flex-1 lg:pl-6">
            <div className="mb-12 md:mb-16 lg:mt-4 mx-auto lg:max-w-none">
              <h3 className="text-xl font-extrabold text-gray-900 sm:text-3xl sm:tracking-tight lg:text-3xl flex items-start flex-col lg:flex-row">
                <div className="w-44 relative h-12 mb-4">
                  {/* <IG
                    alt="VTCT Training Courses At The Cosmetic College"
                    fill
                    src="https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/1M0VdXobETp4giEjEXVxON/image"
                  /> */}
                </div>
                <span className="mt-2 ml-3">{cat}</span>
              </h3>
            </div>
            <div className="md:mt-6 mx-auto grid gap-12 md:grid-cols-2 xl:grid-cols-2 xl:gap-6 lg:max-w-none flex-1">
              {allItems && allItems.length > 0
                ? allItems.map((course, idx) => {
                    if (
                      course.fields &&
                      course.fields.type &&
                      course.fields.type === "Online Course"
                    ) {
                      return (
                        <OnlineCourseItem
                          key={`item-${course.sys.id}`}
                          course={course}
                          cat={cat}
                          setInterested={setInterested}
                        />
                      );
                    }
                    if (
                      course.fields &&
                      course.fields.type &&
                      course.fields.type === "Article" &&
                      rest.blog
                    ) {
                      return (
                        <ArticleItem
                          key={`item-${course.sys.id}`}
                          course={course}
                          cat={cat}
                          index={idx}
                        />
                      );
                    }
                    return (
                      <CourseItem
                        key={`item-${course.sys.id}`}
                        index={idx}
                        course={course}
                        rest={rest}
                        cat={cat}
                        session={session}
                        router={router}
                      />
                    );
                  })
                : null}
            </div>
            {interestCollection && interestCollection.length > 0 && (
              <div className="md:mt-6 mx-auto grid gap-12 md:grid-cols-2 xl:grid-cols-2 xl:gap-6 lg:max-w-none flex-1">
                {interestCollection && interestCollection.length > 0
                  ? interestCollection.map((course) => {
                      if (
                        course.fields &&
                        course.fields.type &&
                        course.fields.type === "Online Course"
                      ) {
                        return (
                          <OnlineCourseItem
                            key={`item-${course.sys.id}`}
                            course={course}
                            cat={cat}
                            setInterested={setInterested}
                          />
                        );
                      }
                      if (
                        course.fields &&
                        course.fields.type &&
                        course.fields.type === "Article" &&
                        rest.blog
                      ) {
                        return (
                          <ArticleItem
                            key={`item-${course.sys.id}`}
                            course={course}
                            cat={cat}
                            index={0}
                          />
                        );
                      }
                      return (
                        <CourseItem
                          key={`item-${course.sys.id}`}
                          course={course}
                          rest={rest}
                          cat={cat}
                          session={session}
                          router={router}
                          interest
                        />
                      );
                    })
                  : null}
              </div>
            )}
            {nonVtct && nonVtct.length > 0 && (
              <div className="my-12 md:my-16 mx-auto lg:max-w-none">
                <h3 className="text-xl font-extrabold text-gray-900 sm:text-3xl sm:tracking-tight lg:text-3xl">
                  Accredited Masterclasses & CPD Training Courses
                </h3>
              </div>
            )}
            {nonVtct && nonVtct.length > 0 ? (
              <div className="md:mt-6 mx-auto grid gap-12 md:grid-cols-2 xl:grid-cols-2 xl:gap-6 lg:max-w-none flex-1">
                {nonVtct.map((course) => {
                  if (
                    course.fields &&
                    course.fields.type &&
                    course.fields.type === "Online Course"
                  ) {
                    return (
                      <OnlineCourseItem
                        key={`item-${course.sys.id}`}
                        course={course}
                        cat={cat}
                        setInterested={setInterested}
                      />
                    );
                  }
                  if (
                    course.fields &&
                    course.fields.type &&
                    course.fields.type === "Article" &&
                    rest.blog
                  ) {
                    return (
                      <ArticleItem
                        key={`item-${course.sys.id}`}
                        course={course}
                        cat={cat}
                        index={0}
                      />
                    );
                  }
                  return (
                    <CourseItem
                      key={`item-${course.sys.id}`}
                      course={course}
                      rest={rest}
                      cat={cat}
                      session={session}
                      router={router}
                    />
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </section>
      <div className="bg-white mx-auto max-w-7xl w-full px-4 lg:px-8 mb-12">
        <QuizPromo variant="banner" />
      </div>
      {interested && (
        <Modal
          open={interested}
          close={() => setInterested(false)}
          xlarge
          title="Register Interest"
          subTitle={
            interested
              ? `Get notified when ${interested.fields.title} is available`
              : null
          }
        >
          <RegisterInterestForm />
        </Modal>
      )}
    </>
  );
}

export default function Listing({ courses, blog, localArticles = [], ...rest }) {
  const router = useRouter();
  const session = useSession();
  const [interested, setInterested] = useState<any>(false);

  const cat = rest.entry && rest.entry.fields && rest.entry.fields.title;

  const normalizedListingPath = router.asPath
    .split("?")[0]
    .split("#")[0]
    .replace(/\/+$/, "");
  const isAudienceLandingPath = [
    "/courses/beginner",
    "/courses/career-change",
    "/courses/beauty-therapists",
    "/courses/medical-professionals",
  ].includes(normalizedListingPath);
  const showTrainingContextLinks =
    normalizedListingPath.startsWith("/courses/") &&
    normalizedListingPath !== "/courses" &&
    !isAudienceLandingPath;

  // Filter articles when blog is true - include both CMS and local articles
  const cmsArticles = blog && courses?.items ? courses.items.filter(course => 
    course.fields?.type === "Article"
  ) : [];
  
  // Local articles first; drop CMS cards that share the same URL.
  const articles = dedupeArticlesByUrl([...localArticles, ...cmsArticles]);

  const blogArticleCollection = useMemo(() => {
    if (!blog || articles.length === 0) {
      return [];
    }

    return [...articles]
      .map((article) => {
        const theme = inferArticleTheme(article);
        return {
          ...article,
          __theme: theme,
          __themeSlug: slugifyTheme(theme),
          __publishedAt: getArticlePublishedAt(article),
          __publishedLabel: getArticlePublishedLabel(article),
          __excerpt: getArticleExcerpt(article),
          __readTime: getArticleReadTime(article),
        };
      })
      .sort((a, b) => {
        if (a.__publishedAt !== b.__publishedAt) {
          return b.__publishedAt - a.__publishedAt;
        }
        return (a?.fields?.title || "").localeCompare(b?.fields?.title || "");
      });
  }, [articles, blog]);

  const featuredArticle = blogArticleCollection[0] || null;

  const groupedBlogThemes = useMemo(() => {
    if (!blogArticleCollection.length) {
      return [];
    }

    const groups = blogArticleCollection.reduce((acc, article) => {
      const theme = article.__theme || "Guides and Insights";
      const slug = article.__themeSlug || slugifyTheme(theme);

      if (!acc[slug]) {
        acc[slug] = {
          theme,
          slug,
          articles: [],
        };
      }

      acc[slug].articles.push(article);
      return acc;
    }, {});

    return Object.values(groups)
      .sort((a: any, b: any) => {
        if (b.articles.length !== a.articles.length) {
          return b.articles.length - a.articles.length;
        }
        return a.theme.localeCompare(b.theme);
      })
      .map((group: any) => ({
        ...group,
        articles: group.articles.sort(
          (a: any, b: any) => b.__publishedAt - a.__publishedAt
        ),
      }));
  }, [blogArticleCollection]);

  // Process courses - must be done before early returns
  const { pre, post } = useMemo(() => {
    if (!courses?.items) return { pre: [], post: [] };
    
    return courses.items.reduce(
      (c, a) => {
        if (
          a.fields?.categories?.includes("online")
        ) {
          if (a.fields.data?.active) {
            return {
              pre: c.pre,
              post: [...c.post, a],
            };
          }

          return {
            pre: [...c.pre, a],
            post: c.post,
          };
        }

        return {
          pre: c.pre,
          post: [...c.post, a],
        };
      },
      { pre: [], post: [] }
    );
  }, [courses?.items]);

  // Early returns - must be after all hooks
  if (
    rest.entry.fields.url === "/courses/elearning" ||
    rest.entry.fields.url === "/courses/elearning/"
  ) {
    return <OnlineCoursesListing courses={courses} {...rest} />;
  }

  const { data } = rest.entry.fields;

  if (data && data.seperated) {
    return <SeperatedListing courses={courses} blog={blog} localArticles={localArticles} {...rest} />;
  }

  // Show grouped article experience when blog = true
  if (blog && blogArticleCollection.length > 0) {
    return (
      <>
        <section className="relative overflow-hidden bg-gradient-to-b from-brand-50 via-white to-white pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.15),transparent_50%)]" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
            <div className="max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-700">
                Cosmetic College Journal
              </p>
              <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
                VTCT Guides for Course Pathways, Qualifications, and Career Progression
              </h1>
              <p className="mt-4 text-lg text-gray-600">
                Explore practical, UK-focused guides by topic to compare routes,
                understand entry requirements, and choose the right training path faster.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {groupedBlogThemes.map((group: any) => (
                <Link
                  key={`theme-chip-${group.slug}`}
                  href={`/blog/category/${group.slug}/`}
                  className="inline-flex items-center rounded-full border border-brand-200 bg-white px-4 py-2 text-sm font-medium text-brand-700 transition hover:border-brand-400 hover:text-brand-900"
                >
                  {group.theme}
                  <span className="ml-2 rounded-full bg-brand-100 px-2 py-0.5 text-xs text-brand-800">
                    {group.articles.length}
                  </span>
                </Link>
              ))}
            </div>

            {featuredArticle && (
              <article className="mt-10 overflow-hidden rounded-3xl border border-brand-100 bg-white shadow-xl shadow-brand-100/60">
                <div className="grid gap-0 lg:grid-cols-2">
                  <div className="relative min-h-[240px] lg:min-h-[360px]">
                    {featuredArticle.fields.listingImage ? (
                      <Image
                        fields={featuredArticle.fields.listingImage}
                        alt={featuredArticle.fields.title}
                        fill
                        className="object-cover"
                        priority
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-brand-100 to-brand-50" />
                    )}
                  </div>
                  <div className="flex flex-col justify-between p-6 lg:p-10">
                    <div>
                      <span className="inline-flex items-center rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-800">
                        Featured: {featuredArticle.__theme}
                      </span>
                      <h2 className="mt-4 text-2xl font-bold text-gray-900 sm:text-3xl">
                        <Link href={featuredArticle.fields.url}>
                          {featuredArticle.fields.title}
                        </Link>
                      </h2>
                      <p className="mt-4 text-base leading-relaxed text-gray-600">
                        {featuredArticle.__excerpt ||
                          "Read the full guide for practical steps, route planning, and next actions."}
                      </p>
                    </div>
                    <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span>{featuredArticle.__publishedLabel}</span>
                      <span className="h-1 w-1 rounded-full bg-gray-300" />
                      <span>{featuredArticle.__readTime} min read</span>
                      <Link
                        href={featuredArticle.fields.url}
                        className="ml-auto inline-flex items-center rounded-md bg-brand-600 px-4 py-2 font-semibold text-white transition hover:bg-brand-700"
                      >
                        Read featured guide
                        <ArrowRightCircleIcon className="ml-2 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            )}

            <div className="mt-14 space-y-12">
              {groupedBlogThemes.map((group: any) => (
                <section
                  key={`theme-section-${group.slug}`}
                  id={`theme-${group.slug}`}
                  className="scroll-mt-24"
                >
                  <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {group.theme}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {group.articles.length} article
                        {group.articles.length === 1 ? "" : "s"} in this theme
                      </p>
                    </div>
                    <Link
                      href={`/blog/category/${group.slug}/`}
                      className="inline-flex items-center text-sm font-semibold text-brand-700 transition hover:text-brand-900"
                    >
                      Open category page
                      <ArrowRightCircleIcon className="ml-2 w-4" />
                    </Link>
                  </div>

                  <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {group.articles.map((article: any, idx: number) => (
                      <article
                        key={`grouped-article-${article.sys.id}`}
                        className="group overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                      >
                        <Link href={article.fields.url} className="block">
                          <div className="relative h-44 w-full overflow-hidden bg-gray-100">
                            {article.fields.listingImage ? (
                              <Image
                                fields={article.fields.listingImage}
                                alt={article.fields.title}
                                fill
                                priority={idx < 2}
                                className="object-cover transition duration-300 group-hover:scale-105"
                              />
                            ) : (
                              <div className="h-full w-full bg-gradient-to-br from-gray-100 to-gray-50" />
                            )}
                          </div>
                        </Link>
                        <div className="p-5">
                          <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
                            {article.__theme}
                          </p>
                          <h4 className="mt-2 text-lg font-semibold leading-snug text-gray-900">
                            <Link href={article.fields.url}>
                              {article.fields.title}
                            </Link>
                          </h4>
                          <p className="mt-3 text-sm leading-relaxed text-gray-600">
                            {article.__excerpt ||
                              "Read this article for practical route planning and next-step guidance."}
                          </p>
                          <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                            <span>{article.__publishedLabel}</span>
                            <span>{article.__readTime} min read</span>
                          </div>
                          <Link
                            href={article.fields.url}
                            className="mt-4 inline-flex items-center text-sm font-semibold text-brand-700 transition hover:text-brand-900"
                          >
                            Read article
                            <ArrowRightCircleIcon className="ml-2 w-4" />
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </section>
        <div className="bg-white mx-auto max-w-7xl w-full px-4 lg:px-8 mb-8">
          <QuizPromo variant="banner" />
        </div>
      </>
    );
  }

  const groupedCourses = post
    ?.filter((x) => !isListingHiddenCourse(x))
    .reduce(
      (a, item) => {
        const category = item.fields.categories?.[0] || "other";
        a[category] = a[category] || [];
        a[category].push(item);
        return a;
      },
      { other: [] }
    );

  return (
    <>
      <section className="container mx-auto relative bg-gray-50 pb-20 px-4 sm:px-6 lg:pt-0 lg:pb-28">
        <div className="absolute inset-0">
          <div className="bg-white h-1/3 sm:h-2/3"></div>
        </div>
        <div className="relative max-w-7xl mx-auto w-full">
          {showTrainingContextLinks ? (
            <div className="mb-6 flex flex-col gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm sm:flex-row sm:items-center sm:gap-6">
              <Link href={TRAINING_HUB_PATH} className="font-medium text-brand-700 underline-offset-4 hover:underline">
                Not sure which course matches your background?
              </Link>
              <Link href="/business-training/" className="font-medium text-brand-700 underline-offset-4 hover:underline">
                Training staff?
              </Link>
            </div>
          ) : null}
          <div className="flex">
          <div className="hidden lg:block sticky lg:top-0 lg:mr-8 lg:w-1/5 lg:mt-6 lg:-ml-4">
            {/* <h3 className="text-lg font-extrabold text-gray-900 sm:tracking-tight mb-4">
              {cat}
            </h3> */}
            <ul className="space-y-3">
              {Object.keys(groupedCourses)
                .sort()
                .map((key) => {
                  const nKey =
                    key === "vtct"
                      ? "VTCT"
                      : key === "packages"
                      ? "Training Packages"
                      : key;

                  return (
                    <Fragment key={`group-${key}`}>
                      {key !== "other" ? (
                        <Link
                          href={resolveCourseCategoryHubPath(key)}
                          className="font-semibold block mt-3"
                        >
                          {replaceHyphensAndCapitalize(nKey)}
                        </Link>
                      ) : (
                        ""
                      )}
                      {groupedCourses[key]
                        .sort((a, b) => {
                          // Sort first by level (number), then by title
                          const levelA = a.fields.level ?? null;
                          const levelB = b.fields.level ?? null;
                          
                          // If both are numbers, compare numerically
                          if (typeof levelA === 'number' && typeof levelB === 'number') {
                            if (levelA !== levelB) {
                              return levelA - levelB;
                            }
                          }
                          // If one is null/undefined and the other is a number, numbers come first
                          else if (typeof levelA === 'number' && levelB === null) {
                            return -1;
                          }
                          else if (levelA === null && typeof levelB === 'number') {
                            return 1;
                          }
                          // If both are null/undefined, they're equal for level comparison
                          
                          // Then sort by title
                          return a.fields.title.localeCompare(b.fields.title);
                        })
                        .map((course) => {
                          return (
                            <li
                              key={`list-${course.sys.id}`}
                              className="text-xs"
                            >
                              <Link
                                href={courseListingHref(course.fields.url)}
                                className="hover:text-brand-500 transition-colors duration-150"
                              >
                                {course.fields.title}
                              </Link>
                            </li>
                          );
                        })}
                    </Fragment>
                  );
                })}
            </ul>
          </div>
          <div className="md:mt-6 mx-auto grid gap-12 md:grid-cols-2 xl:grid-cols-2 xl:gap-6 lg:max-w-none flex-1">
            {post && post.length > 0
              ? post
                  .filter((x) => !isListingHiddenCourse(x))
                  .sort((a, b) => {
                    // Sort first by level (number), then by title
                    const levelA = a.fields.level ?? null;
                    const levelB = b.fields.level ?? null;
                    
                    // If both are numbers, compare numerically
                    if (typeof levelA === 'number' && typeof levelB === 'number') {
                      if (levelA !== levelB) {
                        return levelA - levelB;
                      }
                    }
                    // If one is null/undefined and the other is a number, numbers come first
                    else if (typeof levelA === 'number' && levelB === null) {
                      return -1;
                    }
                    else if (levelA === null && typeof levelB === 'number') {
                      return 1;
                    }
                    // If both are null/undefined, they're equal for level comparison
                    
                    // Then sort by title
                    return a.fields.title.localeCompare(b.fields.title);
                  })
                  .map((course) => {
                    if (
                      course.fields &&
                      course.fields.type &&
                      course.fields.type === "Online Course"
                    ) {
                      return (
                        <OnlineCourseItem
                          key={`item-${course.sys.id}`}
                          course={course}
                          cat={cat}
                          setInterested={setInterested}
                        />
                      );
                    }
                    if (
                      course.fields &&
                      course.fields.type &&
                      course.fields.type === "Article" &&
                      blog
                    ) {
                      return (
                        <ArticleItem
                          key={`item-${course.sys.id}`}
                          course={course}
                          cat={cat}
                          index={0}
                        />
                      );
                    }
                    return (
                      <CourseItem
                        key={`item-${course.sys.id}`}
                        course={course}
                        rest={rest}
                        cat={cat}
                        session={session}
                        router={router}
                      />
                    );
                  })
              : null}
          </div>
          </div>
        </div>
        {pre && pre.length > 0 ? (
          <div className="relative max-w-7xl mx-auto">
            <div className="my-12 md:my-16 mx-auto lg:max-w-none">
              <h3 className="text-xl font-extrabold text-gray-900 sm:text-3xl sm:tracking-tight lg:text-3xl">
                New Courses Coming Soon!
              </h3>
            </div>
            <div className="md:mt-6 mx-auto grid gap-12 md:grid-cols-2 lg:grid-cols-2 lg:max-w-none">
              {pre.map((course) => {
                if (
                  course.fields &&
                  course.fields.type &&
                  course.fields.type === "Online Course"
                ) {
                  return (
                    <OnlineCourseItem
                      key={`item-${course.sys.id}`}
                      course={course}
                      cat={cat}
                      setInterested={setInterested}
                    />
                  );
                }
                if (
                  course.fields &&
                  course.fields.type &&
                  course.fields.type === "Article" &&
                  blog
                ) {
                  return (
                    <ArticleItem
                      key={`item-${course.sys.id}`}
                      course={course}
                      cat={cat}
                      index={0}
                    />
                  );
                }
                return (
                  <CourseItem
                    key={`item-${course.sys.id}`}
                    course={course}
                    rest={rest}
                    cat={cat}
                    session={session}
                    router={router}
                  />
                );
              })}
            </div>
          </div>
        ) : null}
      </section>
      {interested && (
        <Modal
          open={interested}
          close={() => setInterested(false)}
          xlarge
          title="Register Interest"
          subTitle={
            interested
              ? `Get notified when ${interested.fields.title} is available`
              : null
          }
        >
          <RegisterInterestForm />
        </Modal>
      )}
      <div className="bg-white mx-auto max-w-7xl w-full px-4 lg:px-8 mb-6">
        <QuizPromo variant="banner" />
      </div>
    </>
  );
}

function replaceHyphensAndCapitalize(str) {
  return str
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
